<template>
  <section class="section pricing-table" :data-currency="currency">
    <div class="container">
      <div class="pricing-table__wrapper" :data-visible="visible">
        <h2 ref="title" class="section__title pricing-table__title">
          <div class="pricing-table__toggle" @click="handleToggle">
            <span v-html="visible ? translations['toggle']['visible'] : translations['toggle']['hidden']" />
            <svg width="39" height="26" viewBox="0 0 39 26" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.10938 4L19.6094 19L35.1094 4" stroke="#534bc3" stroke-width="9" />
            </svg>
          </div>
        </h2>

        <div
          ref="contents"
          class="pricing-table__contents"
          :style="visible ? contentsVisibleStyles : contentsHiddenStyles"
        >
          <div class="pricing-table__tabs">
            <tabs
              :tabs="[{ label: translations['tabs']['full'] }, { label: translations['tabs']['differences'] }]"
              tabs-type="switch"
              @tab="handleTab"
            />
          </div>

          <article v-if="visibleItems.length" ref="groups" class="pricing-table__groups">
            <!-- Header, mobile/tablet: sticky, without prices, desktop: static, with prices -->
            <header ref="headerTariffs" class="pricing-table-group pricing-table-group--header-tariffs">
              <div class="pricing-table-group__table" role="table">
                <div class="pricing-table-group__row pricing-table-group__row--tariffs" role="row">
                  <div v-if="media.lgMatches" class="pricing-table-group__label" role="cell">
                    <h2 class="text" v-html="translations['tariffs']['title']" />
                  </div>
                  <div v-if="isStartTariffVisible" class="pricing-table-group__cell" role="cell">
                    <span class="text" v-html="translations['tariffs']['small']" />
                  </div>
                  <div class="pricing-table-group__cell" role="cell">
                    <span v-if="media.smMatches" class="text" v-html="translations['tariffs']['professionalShort']" />
                    <span v-if="!media.smMatches" class="text" v-html="translations['tariffs']['professional']" />
                  </div>
                  <div class="pricing-table-group__cell pricing-table-group__cell--maximum" role="cell">
                    <span v-if="media.smMatches" class="text" v-html="translations['tariffs']['maximumShort']" />
                    <span v-if="!media.smMatches" class="text" v-html="translations['tariffs']['maximum']" />
                  </div>
                  <div class="pricing-table-group__cell" role="cell">
                    <span class="text" v-html="translations['tariffs']['enterprise']"></span>
                  </div>
                </div>
                <div
                  v-if="media.lgMatches"
                  class="pricing-table-group__row pricing-table-group__row--prices"
                  role="row"
                >
                  <div class="pricing-table-group__label" role="cell">
                    <span class="text" v-html="translations['price']['title']" />
                  </div>
                  <div v-if="isStartTariffVisible" class="pricing-table-group__cell" role="cell">
                    <price :tariff="'small'" :period="'year'" :currency="currency" />
                    <span class="text" v-html="translations['price']['caption']" />
                  </div>
                  <div class="pricing-table-group__cell" role="cell">
                    <price :tariff="'professional'" :period="'year'" :currency="currency" />
                    <span class="text" v-html="translations['price']['caption']" />
                  </div>
                  <div class="pricing-table-group__cell" role="cell">
                    <price :tariff="'maximum'" :period="'year'" :currency="currency" />
                    <span class="text" v-html="translations['price']['caption']" />
                  </div>
                  <div class="pricing-table-group__cell pricing-table-group__cell--enterprise" role="cell">
                    <span class="text" v-html="translations['price']['enterprise']" />
                  </div>
                </div>
              </div>
            </header>

            <!-- Header, mobile/tablet: static, with prices, desktop: sticky, with prices -->
            <header ref="headerPrices" class="pricing-table-group pricing-table-group--header-prices">
              <div ref="headerPricesOuter">
                <div ref="headerPricesInner">
                  <div class="pricing-table-group__table" role="table">
                    <div v-if="!media.lgMatches" class="group-title-mobile" role="cell">
                      <span class="text" v-html="translations['price']['title']" />
                    </div>
                    <div class="pricing-table-group__row pricing-table-group__row--tariffs" role="row">
                      <div v-if="media.lgMatches" class="pricing-table-group__label" role="cell">
                        <span class="text" v-html="translations['tariffs']['title']" />
                      </div>
                      <div v-if="isStartTariffVisible" class="pricing-table-group__cell" role="cell">
                        <span v-if="media.lgMatches" class="text" v-html="translations['tariffs']['small']" />
                        <price :tariff="'small'" :period="'year'" :currency="currency"
                          >/{{ translations['year'] }}</price
                        >
                      </div>
                      <div class="pricing-table-group__cell" role="cell">
                        <span v-if="media.lgMatches" class="text" v-html="translations['tariffs']['professional']" />
                        <price :tariff="'professional'" :period="'year'" :currency="currency"
                          >/{{ translations['year'] }}</price
                        >
                      </div>
                      <div class="pricing-table-group__cell pricing-table-group__cell--maximum" role="cell">
                        <span v-if="media.lgMatches" class="text" v-html="translations['tariffs']['maximum']" />
                        <price :tariff="'maximum'" :period="'year'" :currency="currency"
                          >/{{ translations['year'] }}</price
                        >
                      </div>
                      <div class="pricing-table-group__cell" role="cell">
                        <span v-if="media.lgMatches" class="price" v-html="translations['price']['enterpriseSticky']" />
                        <span v-else-if="media.mdMatches" class="price" v-html="translations['price']['enterprise']" />
                        <span v-else class="price" v-html="translations['price']['enterpriseShort']" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            <!-- Header recruiters -->
            <header ref="headerRecruiters" class="pricing-table-group pricing-table-group--header-recruiters">
              <div class="pricing-table-group__table" role="table">
                <div class="pricing-table-group__row pricing-table-group__row--recruiters" role="row">
                  <div v-if="media.lgMatches" class="pricing-table-group__label" role="cell">
                    <span class="text" v-html="translations['recruiters']['title']" />
                  </div>
                  <div v-if="!media.lgMatches" class="group-title-mobile" role="cell">
                    <span class="text" v-html="translations['recruiters']['title']" />
                  </div>
                  <div v-if="isStartTariffVisible" class="pricing-table-group__cell" role="cell">
                    <span class="text" v-html="translations['recruiters']['small']" />
                  </div>
                  <div class="pricing-table-group__cell" role="cell">
                    <span class="text" v-html="translations['recruiters']['professional']" />
                  </div>
                  <div class="pricing-table-group__cell" role="cell">
                    <span class="text" v-html="translations['recruiters']['maximum']" />
                  </div>
                  <div class="pricing-table-group__cell" role="cell">
                    <span class="text" v-html="translations['recruiters']['enterprise']" />
                  </div>
                </div>
              </div>
            </header>

            <template v-for="(section, i) in visibleItems">
              <section class="pricing-table-group">
                <h3 :key="`title-${i}`" v-html="section.title" class="pricing-table-group__title" />

                <div class="pricing-table-group__table" role="table">
                  <div
                    v-for="(row, j) in section.items"
                    :key="`row-${i}-${j}`"
                    class="pricing-table-group__row"
                    role="row"
                  >
                    <div class="pricing-table-group__label" role="cell">
                      <span v-if="row.hint || row.additionalPay" class="text">
                        <span v-html="labelContentSplit(row)" />
                        <span class="pricing-table-hints">
                          <span v-html="labelContentSplit(row, true)" />
                          <span class="pricing-table-hints__inner" :aria-label="translations['hint']">
                            <pricing-table-hint v-if="row.hint" />
                            <pricing-table-hint v-if="row.additionalPay" :icon="currency" />
                            <div class="pricing-table-hints__popup">
                              <div v-html="hintContent(row)"></div>
                              <p v-if="row.additionalPay && !row.price" class="row-additional-pay">
                                <pricing-table-hint :icon="currency" />
                                <span v-html="`${translations['additionalPay']}.`" />
                              </p>
                            </div>
                          </span>
                        </span>
                      </span>
                      <span v-else class="text">
                        <span v-html="row.label"></span>
                      </span>
                    </div>
                    <div v-for="(option, k) in row.options" :key="k" class="pricing-table-group__cell" role="cell">
                      <span v-if="typeof option === 'string'" class="text text--limited" v-html="option" />
                      <span
                        v-if="option === Infinity"
                        class="text text--unlimited"
                        v-html="translations['options']['unlimited']"
                      />
                      <span v-if="option === null" class="icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
                          <path stroke="#FF8777" stroke-linecap="round" stroke-width="3" d="m4 4 8 8M12 4l-8 8" />
                        </svg>
                      </span>
                      <span v-if="option === true" class="icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
                          <path
                            stroke="#16BF54"
                            stroke-linecap="round"
                            stroke-width="3"
                            d="m2 7.76453 4 4.23527M14 4l-7.55556 8"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </section>
            </template>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { BREAKPOINTS } from '~scripts/core/breakpoints';
import { formatNumber } from '~scripts/core/helpers';
import { scrollAnimationProgress } from '~scripts/core/animations';
import scrollTo from '~scripts/core/scrollTo';
import Tabs from '~scripts/components/vue/Tabs.vue';
import Price from '../Price/Price.vue';
import priceData from '../Price/price-data.json';
import PricingTableHint from './PricingTableHint.vue';
import pricingData from './pricing-data';
import translations from './pricing-translations';

export default {
  name: 'PricingTable',
  components: { Tabs, Price, PricingTableHint },
  props: {
    currency: {
      type: String,
      default: 'rub',
    },
    lang: {
      type: String,
      default: 'ru',
    },
  },
  data() {
    return {
      media: {
        smMatches: null,
        mdMatches: null,
        lgMatches: null,
      },
      stopHeaderShowAnimation: null,
      stopHeaderHideAnimation: null,
      contentsHiddenStyles: {
        overflow: 'hidden',
        maxHeight: '0',
        opacity: '0',
      },
      contentsVisibleStyles: {
        overflow: 'visible',
        maxHeight: '20000px',
        opacity: '1',
      },
      visible: false,
      unique: false,
    };
  },
  computed: {
    translations() {
      return translations[this.lang];
    },
    breakpoints() {
      return BREAKPOINTS;
    },
    mediaSm() {
      return window.matchMedia(`(max-width: ${this.breakpoints.smMax})`);
    },
    mediaMd() {
      return window.matchMedia(`(min-width: ${this.breakpoints.mdMin}) and (max-width: ${this.breakpoints.mdMax})`);
    },
    mediaLg() {
      return window.matchMedia(`(min-width: ${this.breakpoints.lgMin})`);
    },
    groups() {
      return this.$refs.groups;
    },
    headerTariffs() {
      return this.$refs.headerTariffs;
    },
    items() {
      const items = this.lang === 'en' ? pricingData['en'] : pricingData['ru'];

      if (this.lang === 'ru' && this.currency !== 'rub' && this.currency !== 'kzt' && this.currency !== 'uzs') {
        return items.map((section) => {
          return {
            ...section,
            items: section.items.map((item) => {
              return {
                ...item,
                options: item.options.slice(1),
              };
            }),
          };
        });
      }

      return items;
    },
    uniqueItems() {
      return this.items
        .map((section) => {
          return {
            ...section,
            items: section.items.filter((item) => {
              return new Set(item.options).size > 1;
            }),
          };
        })
        .filter((section) => section.items.length > 0);
    },
    visibleItems() {
      if (!this.unique) {
        return this.items;
      }
      return this.uniqueItems;
    },
    isStartTariffVisible() {
      const { currency, lang } = this;
      return lang === 'ru' && (currency === 'rub' || currency === 'kzt' || currency === 'uzs');
    },
  },
  mounted() {
    this.setMediaSm();
    this.setMediaMd();
    this.setMediaLg();
    this.mediaSm.addEventListener('change', this.setMediaSm, { passive: true });
    this.mediaMd.addEventListener('change', this.setMediaMd, { passive: true });
    this.mediaLg.addEventListener('change', this.setMediaLg, { passive: true });

    window.addEventListener('resize', this.handleResize, { passive: true });

    setTimeout(() => {
      this.handleHeaderShowAnimation();
      this.handleHeaderHideAnimation();
    }, 0);
  },
  beforeDestroy() {
    this.mediaSm.removeEventListener('change', this.setMediaSm);
    this.mediaMd.removeEventListener('change', this.setMediaMd);
    this.mediaLg.removeEventListener('change', this.setMediaLg);

    window.removeEventListener('resize', this.handleResize);

    this.headerShowAnimationStop();
    this.headerHideAnimationStop();
  },
  methods: {
    handleResize() {
      setTimeout(() => {
        if (this.media.lgMatches) {
          this.handleHeaderShowAnimation();
          this.handleHeaderHideAnimation();
        } else {
          this.headerShowAnimationStop();
          this.headerHideAnimationStop();
        }
      }, 0);
    },
    setMediaSm() {
      this.media.smMatches = this.mediaSm.matches;
    },
    setMediaMd() {
      this.media.mdMatches = this.mediaMd.matches;
    },
    setMediaLg() {
      this.media.lgMatches = this.mediaLg.matches;
    },
    scrollToTable() {
      const contentsRect = this.$refs.contents.getBoundingClientRect();
      const scrollOffset = this.mediaSm.matches ? 62 : 70;
      scrollTo(contentsRect.top + window.scrollY - scrollOffset, { duration: 600 });
    },
    handleTab(current) {
      this.unique = current === 1;

      this.scrollToTable();
    },
    handleToggle() {
      this.visible = !this.visible;

      if (this.visible) {
        this.scrollToTable();
      }

      this.handleHeaderShowAnimation();
      this.handleHeaderHideAnimation();
    },
    headerShowAnimationStart() {
      const headerTariffs = this.headerTariffs;
      const offsetTop = this.media.smMatches ? 64 : 72;

      this.stopHeaderShowAnimation = scrollAnimationProgress(this.$refs.headerPricesOuter, {
        getStart() {
          const top = headerTariffs.getBoundingClientRect().top;
          const height = headerTariffs.offsetHeight;
          return top + height + window.scrollY - offsetTop;
        },
        getEnd(el, start) {
          const height = el.offsetHeight;
          return start + height;
        },
        lerpValue: 1,
      });
    },
    headerShowAnimationStop() {
      let { stopHeaderShowAnimation } = this;

      if (stopHeaderShowAnimation && typeof stopHeaderShowAnimation === 'function') {
        stopHeaderShowAnimation();
      }

      stopHeaderShowAnimation = null;
    },
    handleHeaderShowAnimation() {
      if (this.media.lgMatches) {
        if (this.visible) {
          this.headerShowAnimationStart();
        } else {
          this.headerShowAnimationStop();
        }
      }
    },
    headerHideAnimationStart() {
      const groups = this.groups;
      const offsetTop = this.media.smMatches ? 64 : 72;

      this.stopHeaderHideAnimation = scrollAnimationProgress(this.$refs.headerPricesInner, {
        getStart(el) {
          const top = groups.getBoundingClientRect().top;
          const groupsHeight = groups.offsetHeight;
          const elHeight = el.offsetHeight;
          return top + groupsHeight - elHeight + window.scrollY - offsetTop;
        },
        getEnd(el, start) {
          const height = el.offsetHeight;
          return start + height;
        },
        lerpValue: 1,
      });
    },
    headerHideAnimationStop() {
      let { stopHeaderHideAnimation } = this;

      if (stopHeaderHideAnimation && typeof stopHeaderHideAnimation === 'function') {
        stopHeaderHideAnimation();
      }

      stopHeaderHideAnimation = null;
    },
    handleHeaderHideAnimation() {
      if (this.media.lgMatches) {
        if (this.visible) {
          this.headerHideAnimationStart();
        } else {
          this.headerHideAnimationStop();
        }
      }
    },
    labelContentSplit(row, last = false) {
      const { label } = row;

      if (last) {
        const words = label.split(' ');
        return words[words.length - 1];
      }

      const lastIndex = label.lastIndexOf(' ');
      return label.substring(0, lastIndex);
    },
    hintContentDisplay(display = '') {
      setTimeout(function () {
        let hintLawStr = document.querySelectorAll('.js-pricing-table-hint-ru');
        hintLawStr.forEach((item) => (item.style.display = display));
      }, 10);
    },
    hintContent(row) {
      const { hint, price } = row;
      const { currency } = this;
      let result = hint;
      if (price) {
        result = result.replace(
          '%PRICE%',
          `<span class="price">${formatNumber(price[currency])} ${priceData.currencies[currency]}</span>`
        );
      }
      if (currency === 'kzt' || currency === 'uzs') {
        this.hintContentDisplay('none');
      } else {
        this.hintContentDisplay();
      }
      return result;
    },
  },
};
</script>
